import React, { useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, FileText, User, Shield, ArrowRight, Star, Heart, Briefcase, Sparkles, Zap, Target, Compass, MessageSquare, Video, Download, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Accordion } from '../components/ui/Accordion';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Rating } from '../components/ui/Rating';
import { AdSlot } from '../components/ads/AdSlot';
interface ServicePageProps {
  type: 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast' | 'consultation';
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ServicePage({
  type,
  onNavigate
}: ServicePageProps) {
  const [format, setFormat] = useState<'auto' | 'consult'>('auto');
  const formRef = useRef<HTMLDivElement>(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const content: Record<string, {
    title: string;
    badge: string;
    desc: string;
    priceAuto: string;
    priceConsult: string;
    rating: number;
    reviewsCount: number;
    benefits: {
      icon: React.ReactNode;
      title: string;
      desc: string;
    }[];
    extraFields: boolean;
    reviews: {
      name: string;
      text: string;
    }[];
  }> = {
    natal: {
      title: 'Натальная карта',
      badge: 'Хит продаж',
      desc: 'Глубокий анализ вашей личности, заложенных талантов, кармических задач и потенциала во всех сферах жизни. Узнайте, кто вы на самом деле и как реализовать свой потенциал на 100%.',
      priceAuto: '3 900 ₽',
      priceConsult: 'от 4 990 ₽',
      rating: 4.9,
      reviewsCount: 1240,
      benefits: [{
        icon: <User className="w-6 h-6 text-indigo-500" data-id="element-23874" />,
        title: 'Ядро личности',
        desc: 'Ваши сильные и слабые стороны, истинные потребности и скрытые таланты.'
      }, {
        icon: <Briefcase className="w-6 h-6 text-emerald-500" data-id="element-23875" />,
        title: 'Финансы и карьера',
        desc: 'В каких сферах вас ждет успех, как пробить финансовый потолок и где ваши деньги.'
      }, {
        icon: <Heart className="w-6 h-6 text-rose-500" data-id="element-23876" />,
        title: 'Отношения',
        desc: 'Какой партнер вам нужен, почему не складываются отношения и как это исправить.'
      }, {
        icon: <Compass className="w-6 h-6 text-amber-500" data-id="element-23877" />,
        title: 'Кармические задачи',
        desc: 'С чем вы пришли в этот мир, ваши главные уроки и вектор развития (Лунные узлы).'
      }],
      extraFields: false,
      reviews: [{
        name: 'Анна С.',
        text: 'Отчет просто невероятный! 35 страниц подробнейшего разбора. Читала и плакала — настолько точно описаны мои внутренние переживания и страхи. Теперь понятно, в каком направлении двигаться в карьере.'
      }, {
        name: 'Михаил В.',
        text: 'Скептически относился к астрологии, но жена уговорила заказать. Был поражен точностью описания моего характера и подхода к деньгам. Очень полезный инструмент для самопознания.'
      }, {
        name: 'Елена К.',
        text: 'Заказывала авто-отчет, пришел мгновенно. Очень красиво оформлен, все структурировано. Особенно полезна была часть про кармические узлы — многое встало на свои места.'
      }]
    },
    solar: {
      title: 'Соляр (Прогноз на год)',
      badge: 'Важно перед днем рождения',
      desc: 'Подробный прогноз событий на ваш личный год (от дня рождения до дня рождения). Узнайте, где соломку подстелить, а где действовать активно.',
      priceAuto: '4 400 ₽',
      priceConsult: 'от 5 990 ₽',
      rating: 4.8,
      reviewsCount: 856,
      benefits: [{
        icon: <Target className="w-6 h-6 text-indigo-500" data-id="element-23878" />,
        title: 'Главная тема года',
        desc: 'На чем будет сфокусировано ваше внимание и куда направить энергию.'
      }, {
        icon: <Zap className="w-6 h-6 text-amber-500" data-id="element-23879" />,
        title: 'Удачные периоды',
        desc: 'Когда лучше менять работу, открывать бизнес или начинать новые отношения.'
      }, {
        icon: <Shield className="w-6 h-6 text-rose-500" data-id="element-23880" />,
        title: 'Риски и опасности',
        desc: 'Предупреждения о возможных кризисах, травмах или финансовых потерях.'
      }, {
        icon: <Sparkles className="w-6 h-6 text-purple-500" data-id="element-23881" />,
        title: 'Встреча соляра',
        desc: 'Рекомендации, как и где лучше встретить день рождения, чтобы заложить позитивный сценарий.'
      }],
      extraFields: true,
      reviews: [{
        name: 'Ольга П.',
        text: 'Заказываю соляр уже третий год подряд. Это моя настольная книга на год. Всегда знаю, в какие месяцы лучше не планировать важные сделки.'
      }, {
        name: 'Дмитрий Н.',
        text: 'Прогноз помог избежать серьезной ошибки в бизнесе. Был предупрежден о рисках в партнерстве в октябре — так и вышло. Спасибо!'
      }, {
        name: 'Светлана М.',
        text: 'Очень понравились рекомендации по встрече дня рождения. Уехала в другой город, как было написано в отчете, и год действительно выдался волшебным!'
      }]
    },
    compatibility: {
      title: 'Совместимость (Синастрия)',
      badge: 'Для пар',
      desc: 'Глубокий анализ отношений между двумя людьми. Узнайте точки притяжения, истинные причины конфликтов и получите ключи к гармонии в вашей паре.',
      priceAuto: '4 900 ₽',
      priceConsult: 'от 6 990 ₽',
      rating: 4.9,
      reviewsCount: 642,
      benefits: [{
        icon: <Heart className="w-6 h-6 text-rose-500" data-id="element-23882" />,
        title: 'Эмоциональная связь',
        desc: 'Насколько вы понимаете друг друга, ваши языки любви и потребности в заботе.'
      }, {
        icon: <Zap className="w-6 h-6 text-amber-500" data-id="element-23883" />,
        title: 'Конфликтные зоны',
        desc: 'Где искрит, почему возникают ссоры и как экологично их разрешать.'
      }, {
        icon: <Briefcase className="w-6 h-6 text-emerald-500" data-id="element-23884" />,
        title: 'Быт и финансы',
        desc: 'Как вы ведете совместный бюджет, кто должен управлять деньгами в паре.'
      }, {
        icon: <Star className="w-6 h-6 text-indigo-500" data-id="element-23885" />,
        title: 'Кармические связи',
        desc: 'Для чего вы встретились, какие уроки должны пройти вместе.'
      }],
      extraFields: false,
      reviews: [{
        name: 'Екатерина и Максим',
        text: 'Разбор помог спасти брак. Мы поняли, что наши ссоры — это просто разный способ выражать эмоции. Получили четкие рекомендации.'
      }, {
        name: 'Ирина Л.',
        text: 'Заказала синастрию с новым молодым человеком. Отчет подтвердил мои опасения насчет его финансовой нестабильности. Очень отрезвляет.'
      }, {
        name: 'Алексей В.',
        text: 'Интересно было почитать про кармическую связь. Действительно, ощущение, что мы знакомы всю жизнь, нашло астрологическое подтверждение.'
      }]
    },
    combo: {
      title: 'Комбо: Натал + Соляр',
      badge: 'Выгода 17%',
      desc: 'Полная картина вашей жизни: глубокий анализ личности плюс подробный прогноз на ваш личный год. Самый выгодный формат для полного погружения.',
      priceAuto: '6 900 ₽',
      priceConsult: 'от 8 990 ₽',
      rating: 5.0,
      reviewsCount: 428,
      benefits: [{
        icon: <User className="w-6 h-6 text-indigo-500" data-id="element-23886" />,
        title: 'Полный анализ личности',
        desc: 'Все сферы: характер, таланты, карьера, отношения, здоровье.'
      }, {
        icon: <Target className="w-6 h-6 text-amber-500" data-id="element-23887" />,
        title: 'Прогноз на год',
        desc: 'Тенденции года, удачные периоды и риски по месяцам.'
      }, {
        icon: <Sparkles className="w-6 h-6 text-emerald-500" data-id="element-23888" />,
        title: 'Два подробных отчета',
        desc: 'Более 60 страниц персональной информации в красивом PDF-формате.'
      }, {
        icon: <Shield className="w-6 h-6 text-rose-500" data-id="element-23889" />,
        title: 'Компенсаторика',
        desc: 'Практические советы по проработке напряженных аспектов.'
      }],
      extraFields: true,
      reviews: [{
        name: 'Марина Д.',
        text: 'Взяла комбо перед днем рождения. Это лучший подарок самой себе! Сначала разобралась в своих базовых настройках, а потом посмотрела, что ждет в этом году.'
      }, {
        name: 'Виктор С.',
        text: 'Очень объемный материал. Читаю уже неделю, постоянно возвращаюсь и нахожу новые инсайты. Своих денег стоит однозначно.'
      }, {
        name: 'Алина П.',
        text: 'Отличная экономия. Если брать по отдельности, выходит сильно дороже. Отчеты пришли на почту через минуту после оплаты.'
      }]
    },
    forecast: {
      title: 'Прогноз (Транзиты)',
      badge: 'Актуально',
      desc: 'Анализ текущих транзитов и ключевых периодов на ближайшие месяцы. Узнайте, когда действовать, а когда выждать.',
      priceAuto: '3 900 ₽',
      priceConsult: 'от 4 990 ₽',
      rating: 4.7,
      reviewsCount: 315,
      benefits: [{
        icon: <Clock className="w-6 h-6 text-indigo-500" data-id="element-23890" />,
        title: 'Ключевые периоды',
        desc: 'Важные даты и окна возможностей на ближайшее время.'
      }, {
        icon: <Briefcase className="w-6 h-6 text-emerald-500" data-id="element-23891" />,
        title: 'Деловая сфера',
        desc: 'Когда запускать проекты, подписывать договоры и просить повышение.'
      }, {
        icon: <Heart className="w-6 h-6 text-rose-500" data-id="element-23892" />,
        title: 'Личная жизнь',
        desc: 'Благоприятные дни для знакомств, свиданий и важных разговоров.'
      }, {
        icon: <AlertCircle className="w-6 h-6 text-amber-500" data-id="element-23893" />,
        title: 'Зоны риска',
        desc: 'Периоды, когда лучше затаиться, не рисковать деньгами и здоровьем.'
      }],
      extraFields: false,
      reviews: [{
        name: 'Елена М.',
        text: 'Пользуюсь транзитным прогнозом для планирования запусков своих курсов. Конверсия реально выше, когда запускаешь на хороших аспектах!'
      }, {
        name: 'Игорь Т.',
        text: 'Коротко, по делу, без воды. Календарь удачных и неудачных дней теперь всегда под рукой.'
      }, {
        name: 'Дарья В.',
        text: 'Помогло пережить сложный период. Понимала, что это временно (шел транзит Сатурна), и было морально легче.'
      }]
    },
    consultation: {
      title: 'Консультация с астрологом',
      badge: 'Премиум',
      desc: 'Живой разговор с профессиональным астрологом по видеосвязи или в чате. Задайте любые волнующие вас вопросы и получите персональные, глубокие ответы.',
      priceAuto: 'от 2 990 ₽',
      priceConsult: 'от 4 990 ₽',
      rating: 5.0,
      reviewsCount: 890,
      benefits: [{
        icon: <MessageSquare className="w-6 h-6 text-indigo-500" data-id="element-23894" />,
        title: 'Ответы на ваши вопросы',
        desc: 'Разбираем именно то, что болит и волнует вас прямо сейчас.'
      }, {
        icon: <User className="w-6 h-6 text-emerald-500" data-id="element-23895" />,
        title: 'Индивидуальный подход',
        desc: 'Астролог синтезирует все показатели карты под вашу конкретную ситуацию.'
      }, {
        icon: <Video className="w-6 h-6 text-rose-500" data-id="element-23896" />,
        title: 'Живое общение',
        desc: 'Возможность уточнять, переспрашивать и вести диалог с экспертом.'
      }, {
        icon: <Download className="w-6 h-6 text-amber-500" data-id="element-23897" />,
        title: 'Запись встречи',
        desc: 'Видеозапись консультации останется с вами навсегда.'
      }],
      extraFields: false,
      reviews: [{
        name: 'Мария К.',
        text: 'Консультация превзошла все ожидания. Астролог ответила на вопросы, которые я даже не успела озвучить. Очень глубокая работа.'
      }, {
        name: 'Александр П.',
        text: 'Брал бизнес-консультацию. Разобрали потенциал партнеров и удачные ниши. Очень структурно и применимо на практике.'
      }, {
        name: 'Юлия С.',
        text: 'После консультации как будто крылья выросли. Поняла свои сильные стороны и перестала себя ругать за то, что мне не свойственно по природе.'
      }]
    }
  };
  const data = content[type] || content.natal;
  const faqs = [{
    id: '1',
    title: 'Чем авто-отчёт отличается от консультации?',
    content: 'Авто-отчёт — это подробный PDF-документ (30-40 страниц), сгенерированный нашим алгоритмом на основе классических трактовок. Консультация — это живое общение с астрологом, который синтезирует информацию и отвечает на ваши конкретные вопросы.'
  }, {
    id: '2',
    title: 'Что делать, если я не знаю точное время рождения?',
    content: 'Для большинства расчетов время рождения критически важно. Если вы знаете время с погрешностью ±30 минут, мы сможем построить карту. Если время неизвестно совсем, рекомендуем заказать услугу «Ректификация» у наших астрологов.'
  }, {
    id: '3',
    title: 'Как быстро я получу авто-отчёт?',
    content: 'Мгновенно. Сразу после успешной оплаты система сгенерирует PDF-файл. Вы сможете скачать его на странице успеха, также он будет отправлен на email и сохранится в личном кабинете.'
  }, {
    id: '4',
    title: 'Безопасно ли оставлять свои данные?',
    content: 'Абсолютно. Мы используем ваши данные исключительно для математического расчета положения планет. Мы не передаем их третьим лицам и не используем для спама.'
  }];
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-23898">
      {/* 1. HERO SECTION */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-20 lg:pb-32" data-id="element-23899">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" data-id="element-23900" />
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-3xl pointer-events-none" data-id="element-23901" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23902">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: data.title
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-23903" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center" data-id="element-23904">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-23905">
              <motion.div variants={fadeUp} className="mb-4 sm:mb-6" data-id="element-23906">
                <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 px-3 py-1 text-sm" data-id="element-23907">
                  {data.badge}
                </Badge>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight" data-id="element-23908">
                {data.title}
              </motion.h1>

              <motion.p variants={fadeUp} className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-8 leading-relaxed max-w-xl" data-id="element-23909">
                {data.desc}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 sm:gap-4 mb-6 sm:mb-10" data-id="element-23910">
                <div className="flex items-center gap-2 bg-white/10 px-3 sm:px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10" data-id="element-23911">
                  <Rating value={data.rating} showCount={false} size="sm" data-id="element-23912" />
                  <span className="font-bold text-white" data-id="element-23913">{data.rating}</span>
                  <span className="text-slate-400 text-xs sm:text-sm" data-id="element-23914">
                    ({data.reviewsCount} отзывов)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-slate-300 text-xs sm:text-sm" data-id="element-23915">
                  <Clock className="w-4 h-4 text-indigo-400" data-id="element-23916" />
                  Готовность от 1 минуты
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4" data-id="element-23917">
                <Button size="lg" onClick={scrollToForm} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-indigo-500/25" data-id="element-23918">
                  Рассчитать карту
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-23919" />
                </Button>
                <div className="flex flex-col justify-center" data-id="element-23920">
                  <span className="text-sm text-slate-400" data-id="element-23921">Стоимость</span>
                  <span className="text-xl font-bold text-white" data-id="element-23922">
                    от {data.priceAuto}
                  </span>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Illustration */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            rotate: -5
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="hidden lg:flex justify-center relative" data-id="element-23923">
              <div className="w-96 h-96 rounded-full border border-white/10 flex items-center justify-center relative" data-id="element-23924">
                <div className="absolute inset-0 rounded-full border border-indigo-500/30 animate-[spin_60s_linear_infinite]" data-id="element-23925" />
                <div className="absolute inset-4 rounded-full border border-purple-500/20 animate-[spin_40s_linear_infinite_reverse]" data-id="element-23926" />
                <div className="w-64 h-64 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-2xl opacity-40 animate-pulse" data-id="element-23927" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-23928">
                  <Star className="w-32 h-32 text-white/80 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" strokeWidth={1} data-id="element-23929" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. BENEFITS SECTION */}
      <div className="py-12 sm:py-20 bg-white" data-id="element-23930">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23931">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23932">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23933">
              Что вы узнаете из разбора
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23934">
              Мы переводим сложный язык звезд на понятный человеческий. Никакой
              воды и общих фраз — только конкретика про вас.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8" data-id="element-23935">
            {data.benefits.map((benefit, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} data-id="element-23936">
                <Card padding="lg" className="h-full border-slate-100" data-id="element-23937">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-slate-50 flex items-center justify-center mb-4 sm:mb-6" data-id="element-23938">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3" data-id="element-23939">
                    {benefit.title}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed" data-id="element-23940">
                    {benefit.desc}
                  </p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* 3. FORMAT COMPARISON */}
      <div className="py-12 sm:py-20 bg-slate-50" data-id="element-23941">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23942">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23943">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23944">
              Выберите удобный формат
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23945">
              Получите мгновенный текстовый отчет или запишитесь на живую
              консультацию к профессиональному астрологу.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto" data-id="element-23946">
            {/* Auto Report */}
            <Card padding="lg" className="relative overflow-hidden border-slate-200" data-id="element-23947">
              <div className="mb-4 sm:mb-6" data-id="element-23948">
                <Badge variant="neutral" className="mb-3 sm:mb-4" data-id="element-23949">
                  Мгновенно
                </Badge>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2" data-id="element-23950">
                  Авто-отчёт (PDF)
                </h3>
                <p className="text-sm sm:text-base text-slate-600" data-id="element-23951">
                  Подробный текстовый файл на 30-40 страниц с расшифровкой всех
                  показателей.
                </p>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 sm:mb-8" data-id="element-23952">
                {data.priceAuto}
              </div>
              <ul className="space-y-3 mb-6 sm:mb-8" data-id="element-23953">
                <li className="flex items-start" data-id="element-23954">
                  <Check className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-23955" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-23956">
                    Готовность за 1 минуту
                  </span>
                </li>
                <li className="flex items-start" data-id="element-23957">
                  <Check className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-23958" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-23959">
                    Красивое оформление, удобно читать
                  </span>
                </li>
                <li className="flex items-start" data-id="element-23960">
                  <Check className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-23961" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-23962">
                    Остается с вами навсегда
                  </span>
                </li>
              </ul>
              <Button variant="outline" fullWidth onClick={() => {
              setFormat('auto');
              scrollToForm();
            }} data-id="element-23963">
                Выбрать PDF-отчёт
              </Button>
            </Card>

            {/* Consultation */}
            <Card padding="lg" className="relative overflow-hidden border-indigo-200 ring-1 ring-indigo-100 shadow-xl shadow-indigo-100/50" data-id="element-23964">
              <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg" data-id="element-23965">
                Рекомендуем
              </div>
              <div className="mb-4 sm:mb-6" data-id="element-23966">
                <Badge variant="pro" className="mb-3 sm:mb-4" data-id="element-23967">
                  Живое общение
                </Badge>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2" data-id="element-23968">
                  Консультация
                </h3>
                <p className="text-sm sm:text-base text-slate-600" data-id="element-23969">
                  Видеозвонок с астрологом. Персональный разбор и ответы на ваши
                  вопросы.
                </p>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 sm:mb-8" data-id="element-23970">
                {data.priceConsult}
              </div>
              <ul className="space-y-3 mb-6 sm:mb-8" data-id="element-23971">
                <li className="flex items-start" data-id="element-23972">
                  <Check className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-23973" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-23974">
                    60-90 минут общения с экспертом
                  </span>
                </li>
                <li className="flex items-start" data-id="element-23975">
                  <Check className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-23976" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-23977">
                    Фокус на ваших конкретных запросах
                  </span>
                </li>
                <li className="flex items-start" data-id="element-23978">
                  <Check className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-23979" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-23980">
                    Видеозапись встречи в подарок
                  </span>
                </li>
              </ul>
              <Button variant="primary" fullWidth onClick={() => {
              setFormat('consult');
              scrollToForm();
            }} data-id="element-23981">
                Выбрать консультацию
              </Button>
            </Card>
          </div>
        </div>
      </div>

      {/* 4. REVIEWS */}
      <div className="py-12 sm:py-20 bg-white" data-id="element-23982">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23983">
          <div className="text-center mb-8 sm:mb-16" data-id="element-23984">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23985">
              Отзывы клиентов
            </h2>
            <div className="flex items-center justify-center gap-2" data-id="element-23986">
              <Rating value={5} showCount={false} data-id="element-23987" />
              <span className="font-bold text-slate-900" data-id="element-23988">{data.rating}</span>
              <span className="text-slate-500 text-sm" data-id="element-23989">
                из 5 на основе {data.reviewsCount} оценок
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-23990">
            {data.reviews.map((review, idx) => <Card key={idx} padding="md" className="bg-slate-50 border-none" data-id="element-23991">
                <div className="flex items-center gap-3 mb-3 sm:mb-4" data-id="element-23992">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold" data-id="element-23993">
                    {review.name[0]}
                  </div>
                  <div data-id="element-23994">
                    <div className="font-bold text-slate-900" data-id="element-23995">
                      {review.name}
                    </div>
                    <Rating value={5} showCount={false} size="sm" data-id="element-23996" />
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed italic" data-id="element-23997">
                  "{review.text}"
                </p>
              </Card>)}
          </div>
        </div>
      </div>

      {/* 5. ORDER FORM SECTION */}
      <div ref={formRef} className="py-12 sm:py-20 bg-slate-900 text-white scroll-mt-10" data-id="element-23998">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23999">
          <div className="text-center mb-8 sm:mb-12" data-id="element-24000">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4" data-id="element-24001">
              Оформление заказа
            </h2>
            <p className="text-slate-400 text-sm sm:text-base" data-id="element-24002">
              Заполните данные для точного астрологического расчета
            </p>
          </div>
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-10 text-slate-900 shadow-2xl" data-id="element-24003">
            {/* Format Toggle */}
            <div className="flex p-1 bg-slate-100 rounded-xl mb-6 sm:mb-8" data-id="element-24004">
              <button className={`flex-1 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-bold transition-all ${format === 'auto' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setFormat('auto')} data-id="element-24005">
                Авто-отчёт ({data.priceAuto})
              </button>
              <button className={`flex-1 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-bold transition-all ${format === 'consult' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setFormat('consult')} data-id="element-24006">
                Консультация ({data.priceConsult})
              </button>
            </div>

            <form className="space-y-4 sm:space-y-6" data-id="element-24007">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-24008">
                <Input label="Имя" placeholder="Как к вам обращаться" data-id="element-24009" />
                <Input label="Дата рождения" type="date" data-id="element-24010" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-24011">
                <Input label="Время рождения" type="time" data-id="element-24012" />
                <Input label="Место рождения" placeholder="Город, страна" data-id="element-24013" />
              </div>

              {data.extraFields && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 pt-4 border-t border-slate-100" data-id="element-24014">
                  <Input label="Год соляра" type="number" placeholder="Например, 2026" defaultValue="2026" data-id="element-24015" />
                  <Input label="Город встречи соляра" placeholder="Где будете в день рождения" data-id="element-24016" />
                </div>}

              {type === 'compatibility' && <>
                  <div className="pt-4 sm:pt-6 border-t border-slate-200" data-id="element-24017">
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-24018">
                      Данные второго партнёра
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-24019">
                    <Input label="Имя партнёра" placeholder="Имя" data-id="element-24020" />
                    <Input label="Дата рождения" type="date" data-id="element-24021" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-24022">
                    <Input label="Время рождения" type="time" data-id="element-24023" />
                    <Input label="Место рождения" placeholder="Город, страна" data-id="element-24024" />
                  </div>
                </>}

              <div className="pt-6 sm:pt-8 mt-6 sm:mt-8 border-t border-slate-100" data-id="element-24025">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6" data-id="element-24026">
                  <div data-id="element-24027">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-24028">
                      Итого к оплате:
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900" data-id="element-24029">
                      {format === 'auto' ? data.priceAuto : data.priceConsult}
                    </div>
                  </div>
                  <Button size="lg" className="w-full md:w-auto px-8 sm:px-10" onClick={e => {
                  e.preventDefault();
                  onNavigate(format === 'auto' ? 'checkout' : 'astrologers');
                }} data-id="element-24030">
                    {format === 'auto' ? 'Оплатить и получить отчёт' : 'Выбрать астролога'}
                  </Button>
                </div>
                <div className="flex items-center justify-center md:justify-end mt-4 text-xs text-slate-500 gap-4" data-id="element-24031">
                  <span className="flex items-center" data-id="element-24032">
                    <Shield className="w-3.5 h-3.5 mr-1 text-emerald-500" data-id="element-24033" />
                    Безопасная оплата
                  </span>
                  <span className="flex items-center" data-id="element-24034">
                    <Clock className="w-3.5 h-3.5 mr-1 text-indigo-500" data-id="element-24035" />
                    {format === 'auto' ? 'Мгновенно' : 'По записи'}
                  </span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Ad Banner */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-24036">
        <AdSlot slotId="SLOT-13" onNavigate={onNavigate} data-id="element-24037" />
      </div>

      {/* 6. FAQ SECTION */}
      <div className="py-12 sm:py-20 bg-slate-50" data-id="element-24038">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-24039">
          <div className="text-center mb-8 sm:mb-12" data-id="element-24040">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-24041">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={faqs} data-id="element-24042" />
        </div>
      </div>
    </div>;
}